import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {KeycloakEventType, KeycloakService} from 'keycloak-angular';
import {KeycloakEvent} from 'keycloak-angular/lib/core/interfaces/keycloak-event';
import {Subscription} from 'rxjs';
import {SessionService} from '../state/session';
import {MIN_TOKEN_VALIDITY_SECONDS, REFRESH_RETRY_DELAYS_MS} from '../constants/token-refresh.const';

/**
 * Тримає access-токен Keycloak свіжим і синхронізує його зі SessionStore.
 *
 * Важливо: тут НІКОЛИ не викликається keycloak.logout() — він знищує SSO-сесію на сервері.
 * Якщо Keycloak відхилив refresh-токен (400), keycloak-js сам очищає токени і робить login():
 * при живій SSO-сесії це тихий редірект без введення пароля.
 */
@Injectable()
export class TokenRefreshService implements OnDestroy {
  private failedAttempts = 0;
  private retryTimeoutId: any = null;
  private started = false;
  private readonly subscriptions = new Subscription();

  private readonly onOnline = () => this.refresh(MIN_TOKEN_VALIDITY_SECONDS);
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.refresh(MIN_TOKEN_VALIDITY_SECONDS);
    }
  }

  constructor(
    private keycloak: KeycloakService,
    private sessionService: SessionService,
    private ngZone: NgZone,
  ) {
  }

  /** Викликається один раз після успішного keycloak.init(). */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.subscriptions.add(
      this.keycloak.keycloakEvents$.subscribe((event: KeycloakEvent) => this.handleEvent(event)),
    );

    // Після сну ноутбука / фонової вкладки таймер onTokenExpired спрацьовує із запізненням,
    // тому оновлюємо токен одразу, щойно вкладка знову видима або повернулась мережа.
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Оновлює токен, якщо він живе менше ніж minValidity секунд. Ніколи не реджектиться. */
  ensureFreshToken(minValidity: number = MIN_TOKEN_VALIDITY_SECONDS): Promise<boolean> {
    if (!this.canRefresh()) {
      return Promise.resolve(false);
    }

    return this.keycloak.updateToken(minValidity)
      .then(() => true)
      .catch(() => false);
  }

  /** Примусово оновлює токен (напр. після 401). Ніколи не реджектиться. */
  forceRefresh(): Promise<boolean> {
    return this.ensureFreshToken(-1);
  }

  getToken(): string {
    const instance = this.keycloak.getKeycloakInstance();
    return (instance && instance.token) || this.sessionService.getToken();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.cancelRetry();
  }

  private handleEvent(event: KeycloakEvent): void {
    switch (event.type) {
      case KeycloakEventType.OnTokenExpired:
        this.refresh();
        break;
      case KeycloakEventType.OnAuthSuccess:
      case KeycloakEventType.OnAuthRefreshSuccess:
        this.persistCurrentToken();
        break;
      case KeycloakEventType.OnAuthLogout:
        // Лише чистимо локальний стан; серверну сесію не чіпаємо.
        this.sessionService.clearToken();
        break;
      default:
        break;
    }
  }

  private refresh(minValidity?: number): void {
    if (!this.canRefresh()) {
      return;
    }

    this.cancelRetry();

    this.keycloak.updateToken(minValidity)
      .then(() => {
        this.failedAttempts = 0;
      })
      .catch(() => {
        const instance = this.keycloak.getKeycloakInstance();

        // Keycloak відхилив refresh-токен (400): keycloak-js уже очистив токени і робить login().
        if (!instance || !instance.token) {
          this.failedAttempts = 0;
          return;
        }

        // Тимчасова помилка (немає мережі, 5xx) — пробуємо ще раз з наростаючою затримкою.
        this.scheduleRetry();
      });
  }

  private canRefresh(): boolean {
    const instance = this.keycloak.getKeycloakInstance();
    return !!instance && !!instance.authenticated && !!instance.refreshToken;
  }

  private persistCurrentToken(): void {
    const instance = this.keycloak.getKeycloakInstance();
    if (instance && instance.token) {
      this.sessionService.saveToken(instance.token);
    }
  }

  private scheduleRetry(): void {
    const lastDelayIndex = REFRESH_RETRY_DELAYS_MS.length - 1;
    const delay = REFRESH_RETRY_DELAYS_MS[Math.min(this.failedAttempts, lastDelayIndex)];

    this.failedAttempts += 1;
    this.ngZone.runOutsideAngular(() => {
      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = null;
        this.ngZone.run(() => this.refresh());
      }, delay);
    });
  }

  private cancelRetry(): void {
    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }
}
